import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { StoryboardShellLayout, CleverStoryStoryboardSettingsPage } from "../storyboardSettings";
import { LeadsAndReportsPage } from "../leadsAndReports";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { toast } from "react-toastify";
import Toast from "src/components/toast";

import { useQueryClient } from "@tanstack/react-query";
import { EXPERIENCES, STORYBOARDS, FETCH } from "src/query/queryKeys";
import { fixCursorSnapOffset } from "src/utils/dnd";
import { useAddStoryboardToList, useRenameExperiencesStoryboard } from "src/query/experiences/storyboards";
import { updateExperiencesStoryboardList } from "src/redux/actions/experiences/storyboardLists";
import { DraggableStoryboardCard } from "./components/storyboardCard";
import { StoryboardListItem } from "src/types/experiences/storyboardLists";

import { fetchExperiencesWorkspace } from "src/redux/actions/experiences/workspaces";
import { fetchExperiencesStoryboardLists } from "src/redux/actions/experiences/storyboardLists";
import { updateCleverstoryWorkspace } from "src/utils/auth.interceptor";
import { WorkspaceSelector } from "./components/workspaceSelector";
import { StoryboardList } from "./components/folderList";
import { StoryboardLists } from "./components/storyboardLists";
import { FilterDropdown } from "../components/filterDropdown";
import { CreateStoryboard } from "../components/createStoryboard";
import {
  ExperiencesMenuBar,
  CloneMenubar,
  DeleteMenubar,
  RenameFolderMenubar,
} from "./components/edit";
import { StoryboardsContext, EditMode, UploadingStoryboard } from "./context";
import { PromptInput } from "./components/promptInput";
import {
  FilterOption,
  STORYBOARD_TYPE_OPTIONS,
  STORYBOARD_STATUS_OPTIONS,
} from "../types";
import { StoryboardItem } from "src/types/experiences/storyboards";
import { useWorkspaceSubscription } from "../hooks/useWorkspaceSubscription";
import { useCanCreateStoryboard } from "../hooks/useCanCreateStoryboard";
import RolesAndPermissions from "../storyboardSettings/components/rolesAndPermissions";

import styles from "./storyboards.module.scss";

const ALL_TYPES = STORYBOARD_TYPE_OPTIONS[0];
const ALL_STATUS = STORYBOARD_STATUS_OPTIONS[0];

const StoryboardsListingPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, listId } = useParams<{
    workspaceId: string;
    listId: string;
  }>();

  const workspaces = useSelector(
    (state: any) => state.experiences.workspaces.items,
  );

  const subscription = useWorkspaceSubscription();

  const canCreateStoryboard = useCanCreateStoryboard();

  const promptBasedEditorEnabled = useSelector(
    (state: any) => state.experiences.workspaces.workspace.item?.promptBasedEditorEnabled
  );

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filterType, setFilterType] = useState<FilterOption>(ALL_TYPES);
  const [filterStatus, setFilterStatus] = useState<FilterOption>(ALL_STATUS);
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [selectedStoryboards, setSelectedStoryboards] = useState<StoryboardItem[]>(
    [],
  );
  const [promptValue, setPromptValue] = useState<string>("");
  const [dragStoryboard, setDragStoryboard] = useState<StoryboardItem | null>(null);
  const [uploads, setUploads] = useState<UploadingStoryboard[]>([]);
  // Staged rename edits (folders + storyboard cards), keyed by id.
  const [renameEdits, setRenameEdits] = useState<
    Record<string, { type: "storyboard" | "folder"; name: string }>
  >({});
  const [renameSaving, setRenameSaving] = useState(false);
  const renameStoryboard = useRenameExperiencesStoryboard(workspaceId!);

  const addUpload = (upload: UploadingStoryboard) => {
    setUploads((prev) => [upload, ...prev]);
  };

  const updateUpload = (id: string, patch: Partial<UploadingStoryboard>) => {
    setUploads((prev) =>
      prev.map((upload) => (upload.id === id ? { ...upload, ...patch } : upload)),
    );
  };

  const removeUpload = (id: string) => {
    setUploads((prev) => {
      const target = prev.find((upload) => upload.id === id);
      if (target?.thumbnailUrl) URL.revokeObjectURL(target.thumbnailUrl);
      return prev.filter((upload) => upload.id !== id);
    });
  };

  const queryClient = useQueryClient();
  const addStoryboardToList = useAddStoryboardToList(workspaceId!);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);

  useEffect(() => {
    const prompt = location.state?.prompt;

    if (!prompt) return;

    setPromptValue(prompt);

    window.history.replaceState({}, document.title, window.location.pathname);
  }, [location.state]);

  // Reset filters + edit mode when workspace changes
  useEffect(() => {
    setFilterType(ALL_TYPES);
    setFilterStatus(ALL_STATUS);
    setEditMode(null);
    setSelectedStoryboards([]);
  }, [workspaceId]);

  const handleWorkspaceSelect = (id: string) => {
    updateCleverstoryWorkspace(id);
    navigate(`/experiences/${id}/storyboards`);
  };

  const handleEditModeChange = (mode: EditMode) => {
    setEditMode(mode);
    setSelectedStoryboards([]);
    setRenameEdits({});
  };

  const handleRenameChange = (
    id: string,
    type: "storyboard" | "folder",
    name: string,
  ) => {
    setRenameEdits((prev) => ({ ...prev, [id]: { type, name } }));
  };

  // Commit every staged rename (multiple folders + storyboard cards) in a loop,
  // then exit rename mode — mirrors the collections Save flow.
  const handleRenameSave = async () => {
    const entries = Object.entries(renameEdits).filter(
      ([, edit]) => edit.name.trim().length > 0,
    );
    if (entries.length === 0) {
      handleEditModeChange(null);
      return;
    }

    setRenameSaving(true);

    // Folders go through redux; the reducer swaps the item on success.
    entries
      .filter(([, edit]) => edit.type === "folder")
      .forEach(([id, edit]) =>
        dispatch(updateExperiencesStoryboardList({ id, name: edit.name.trim() })),
      );

    // Storyboard cards go through the rename mutation (cache-patched).
    try {
      await Promise.all(
        entries
          .filter(([, edit]) => edit.type === "storyboard")
          .map(([id, edit]) =>
            renameStoryboard.mutateAsync({ id, name: edit.name.trim() }),
          ),
      );
    } catch {
      // Individual failures are reconciled by the list query; still close out.
    }

    setRenameSaving(false);
    handleEditModeChange(null);
  };

  const handleSearchQuery = (value) => {
    setSearchQuery(value);
  };

  const handlePromptSubmit = () => {
    const trimmed = promptValue.trim();
    if (!trimmed) {
      toast(
        <Toast.Failure
          heading="Empty prompt"
          message="Please enter a prompt."
        />,
      );
      return;
    }
    navigate("/storyline", {
      state: { prompt: trimmed, workspaceId, listId: listId ?? null },
    });
  };

  const handleStoryboardSelect = (storyboard: StoryboardItem | null) => {
    // null clears everything.
    if (!storyboard) {
      setSelectedStoryboards([]);
      return;
    }
    // Clone works on a single storyboard; delete supports multi-select toggling.
    if (editMode === "clone") {
      setSelectedStoryboards([storyboard]);
      return;
    }
    setSelectedStoryboards((prev) =>
      prev.some((s) => s.id === storyboard.id)
        ? prev.filter((s) => s.id !== storyboard.id)
        : [...prev, storyboard],
    );
  };

  const handleDragStart = (event: any) => {
    const storyboard = _.get(event, "active.data.current.storyboard");
    setDragStoryboard(storyboard);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    const storyboard: StoryboardItem | undefined = _.get(active, "data.current.storyboard");
    const targetList: StoryboardListItem | undefined = _.get(over, "data.current.list");

    if (storyboard && targetList && targetList.id !== listId) {
      addStoryboardToList.mutate(
        { listId: targetList.id, storyboardId: storyboard.id },
        {
          onSuccess: () => {
            // Remove from current folder's cached list immediately after move —
            // skip when in "All" view (listId is null) since it shows everything.
            if (listId) {
              const currentQueryKey = [EXPERIENCES, STORYBOARDS, FETCH, workspaceId!, listId, searchQuery, filterType.value ?? "", filterStatus.value ?? ""];
              queryClient.setQueryData(currentQueryKey, (old: any) => {
                if (!old) return old;
                return {
                  ...old,
                  pages: old.pages.map((page: any) => ({
                    ...page,
                    records: page.records.filter((s: any) => s.id !== storyboard.id),
                    totalRecordsCount: Math.max(0, page.totalRecordsCount - 1),
                  })),
                };
              });
            }

            toast(
              <Toast.Sucess
                message={`Success! Storyboard ${storyboard.name} added to folder ${targetList.name}. Celebrate with a little dance!`}
              />,
            );
          },
          onError: () => {
            toast(
              <Toast.Failure
                heading="Couldn't move storyboard"
                message={`Something went wrong while moving ${storyboard.name} to ${targetList.name}. Please try again.`}
              />,
            );
          },
        },
      );
    }

    setDragStoryboard(null);
  };

  const availableTypeOptions = STORYBOARD_TYPE_OPTIONS.filter((option) => {
    if (!option.subscriptionKey || !subscription) return true;
    return subscription[option.subscriptionKey] === true;
  });

  return (
    <StoryboardsContext.Provider
      value={{
        editMode,
        selectedStoryboards,
        onStoryboardSelect: handleStoryboardSelect,
        onEditModeChange: handleEditModeChange,
        renameEdits,
        onRenameChange: handleRenameChange,
        uploads,
        addUpload,
        updateUpload,
        removeUpload,
      }}
    >
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        autoScroll={false}
        collisionDetection={fixCursorSnapOffset}
        sensors={sensors}
      >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.pageHeader}>
            <h1>experiences</h1>
            <p>everything you created, topped with stardust</p>
          </div>

          <WorkspaceSelector
            workspaces={workspaces}
            selectedId={workspaceId ?? null}
            onSelect={handleWorkspaceSelect}
          />
        </div>

        <CreateStoryboard workspaceId={workspaceId!} listId={listId ?? null} />

        <PromptInput
          value={promptValue}
          onChange={setPromptValue}
          onSubmit={handlePromptSubmit}
          visible={promptBasedEditorEnabled && canCreateStoryboard}
        />
        <div className={styles.actionHeader}>
          <div className={styles.headerContainer}>
            <div className={styles.pageHeader}>
              <div className={styles.primaryHeader}>
                <p>my storyboards</p>
              </div>

              {/* actionMenu — replaces filterSection (line 96), contains all mode bars */}
              <div className={styles.actionMenu}>
                <ExperiencesMenuBar
                  show={!editMode}
                  query={searchQuery}
                  onSearchChange={handleSearchQuery}
                  onEditModeChange={handleEditModeChange}
                />
                <CloneMenubar
                  show={editMode === "clone"}
                  onCancel={() => handleEditModeChange(null)}
                />
                <RenameFolderMenubar
                  show={editMode === "rename"}
                  saving={renameSaving}
                  onSave={handleRenameSave}
                  onCancel={() => handleEditModeChange(null)}
                />
                <DeleteMenubar
                  show={editMode === "delete"}
                  onCancel={() => handleEditModeChange(null)}
                />
              </div>
            </div>

            <div className={styles.subHeader}>
              <StoryboardLists workspaceId={workspaceId!} />
              <div className={styles.filterContainer}>
                <FilterDropdown
                  options={availableTypeOptions}
                  selected={filterType}
                  onSelect={setFilterType}
                />
                <FilterDropdown
                  options={STORYBOARD_STATUS_OPTIONS}
                  selected={filterStatus}
                  onSelect={setFilterStatus}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.content}>
          <StoryboardList
            workspaceId={workspaceId!}
            listId={listId ?? null}
            query={searchQuery}
            type={filterType.value}
            status={filterStatus.value}
          />
        </div>
      </div>

      <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null}>
        {dragStoryboard && <DraggableStoryboardCard storyboard={dragStoryboard} />}
      </DragOverlay>
      </DndContext>
    </StoryboardsContext.Provider>
  );
};

export const StoryboardsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  // Handles all 3 cases: first load (after redirect), direct URL, workspace switch
  useEffect(() => {
    if (!workspaceId) return;
    updateCleverstoryWorkspace(workspaceId);
    dispatch(fetchExperiencesWorkspace(workspaceId));
    dispatch(fetchExperiencesStoryboardLists());
  }, [workspaceId]);

  return (
    <Routes>
      <Route path="/" element={<StoryboardsListingPage />} />
      <Route path="folder/:listId" element={<StoryboardsListingPage />} />
      <Route path=":storyboardId" element={<StoryboardShellLayout />}>
        <Route path="website-settings" element={<CleverStoryStoryboardSettingsPage />} />
        <Route path="permissions" element={<RolesAndPermissions />} />
        <Route path="leads" element={<LeadsAndReportsPage />} />
        <Route path="leads/profile" element={<LeadsAndReportsPage />} />
        <Route path="leads/profile/:profileId" element={<LeadsAndReportsPage />} />
        <Route path="reports" element={<LeadsAndReportsPage />} />
      </Route>
    </Routes>
  );
};
